/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IMenuItemData, MenuTypeIndexId } from '@msdyn365-commerce-modules/navigation-menu';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    addThrottledEvent,
    Button,
    Collapse,
    Drawer,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    ICollapseProps,
    IPayLoad,
    isMobile,
    ITelemetryContent,
    Module,
    Node,
    onTelemetryClick,
    VariantType
} from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import classnames from 'classnames';
import { computed } from 'mobx';
import * as React from 'react';

import { getCategoryImage, INavigationState, INosNavigationMenuViewRootProps, NavMenuConstants } from './navigation-menu-utilities';

/**
 *
 * NavigationMenuLinks component.
 * @extends {React.PureComponent<INavigationMenuViewProps>}
 */
export class NavigationMenuLinks extends React.PureComponent<INosNavigationMenuViewRootProps, INavigationState> {
    private static isBackTrack: boolean = false;

    private currentLevel: number = NavMenuConstants.zero;

    private readonly menuNode: React.RefObject<HTMLElement>;

    private readonly menuItemRef: React.RefObject<HTMLElement>;

    private readonly escapeKey: number = NavMenuConstants.escapeKey;

    private readonly telemetryContent: ITelemetryContent;

    private readonly promotionalImage: Msdyn365.IImageData[] = [];

    private readonly payLoad: IPayLoad;

    private readonly attributes?: Msdyn365.IDictionary<string>;

    private customResizeThrottledEventHandler?: (event: Event) => void;

    public constructor(props: INosNavigationMenuViewRootProps) {
        super(props);
        this.menuNode = React.createRef();
        this.menuItemRef = React.createRef();
        const { menuItemData } = this.props.navProps;
        if (ArrayExtensions.hasElements(menuItemData) && ArrayExtensions.hasElements(menuItemData[0].subMenu)) {
            const imagesource = menuItemData[0].subMenu[0].imageSource ? menuItemData[0].subMenu[0].imageSource : '';
            const linkText = menuItemData[0].subMenu[0].linkText ? menuItemData[0].subMenu[0].linkText : '';
            this.promotionalImage = [{ src: imagesource, altText: linkText }];

            // Read category and promotional image in one array
            if (ArrayExtensions.hasElements(menuItemData[0].subMenu[0].promotionalContent)) {
                menuItemData[0].subMenu[0].promotionalContent.map(item => {
                    const imageSource = item.image.src;
                    this.promotionalImage.push({
                        src: imageSource,
                        altText: item.text,
                        additionalProperties: { linkUrl: item.linkUrl.destinationUrl }
                    });
                    return null;
                });
            }
        }

        this.state = {
            activeMenu: 1,
            mobileViewLabelText: '',
            parentMenu: undefined,
            drawerKeyValue: {},
            isOnlyMobile: false,
            categoryImage: this.promotionalImage,
            isNavOpen: false,
            menuItemId: -1
        };
        this.telemetryContent = getTelemetryObject(
            this.props.navProps.context.request.telemetryPageName!,
            this.props.navProps.friendlyName,
            this.props.navProps.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '', '');
        this.attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
    }

    @computed public get isOnlyMobile(): boolean {
        const screenMode = isMobile({ variant: VariantType.Browser, context: this.props.navProps.context.request });
        return ['xs', 'sm', 'md', 'lg'].includes(screenMode);
    }

    public componentDidMount(): void {
        this.customResizeThrottledEventHandler = addThrottledEvent(window, 'resize', this._customUpdateViewport as EventListener);
        document.addEventListener('keydown', (this._escFunction as unknown) as EventListener, false);
        this._customUpdateViewport();
        document.body.addEventListener('mousedown', this._handleClickOutside);
    }

    public componentDidUpdate(): void {
        if (this.menuItemRef.current?.children[0] && this.props.navProps.isMobileView) {
            (this.menuItemRef.current.children[0] as HTMLElement).focus();
        }
    }

    public componentWillUnmount(): void {
        window.removeEventListener('resize', this.customResizeThrottledEventHandler!, false);
        document.body.removeEventListener('mousedown', this._handleClickOutside, false);
    }

    public render(): JSX.Element | null {
        this.currentLevel = 1;
        const navbarKey = 'header-nav-mobile';

        return (
            <div className={classnames('ms-nav', this.state.isOnlyMobile ? 'ms-nav--mobile' : 'ms-nav--desktop')}>
                <Node className='ms-nav__menu' ref={this.menuNode}>
                    {this.state.isOnlyMobile ? (
                        <>
                            <Button
                                className='ms-nav__hamburger'
                                onClick={this._toggleNavBar}
                                aria-controls={navbarKey}
                                title={this.props.navProps.resources.hamburgerAriaLabel}
                                aria-label={this.props.navProps.resources.hamburgerAriaLabel}
                                {...this.attributes}
                            >
                                <span className='ms-nav__hamburger-box'>
                                    <span className='ms-nav__hamburger-inner'></span>
                                </span>
                            </Button>
                            {this._renderCollapseMenu()}
                        </>
                    ) : (
                        <>
                            {this._createDesktopHeaderLinks()}
                            {this._renderCollapseMenu()}
                        </>
                    )}
                </Node>
            </div>
        );
    }

    /**
     * Function to handle nav bar toggle.
     */
    private readonly _toggleNavBar = (): void => {
        this.setState(previousState => ({
            activeMenu: undefined,
            isNavOpen: !previousState.isNavOpen
        }));
    };

    private _createDesktopHeaderLinks = () => {
        return this.props.navProps?.menuItemData?.length ? (
            <ul>
                {this.props.navProps.menuItemData.map(link => {
                    const isActive = this.state.menuItemId === link.id && this.state.isNavOpen;
                    return link?.subMenu?.length ? (
                        <li>
                            <Button
                                key={link.id}
                                onClick={() => {
                                    if (!this.state.isNavOpen || (this.state.isNavOpen && isActive)) {
                                        this._toggleNavBar();
                                    }
                                    this.setState({ menuItemId: link.id });
                                }}
                                className={isActive ? 'ms-nav-item__active' : ''}
                            >
                                {link.linkText}
                            </Button>
                        </li>
                    ) : (
                        <li>{this._createLink(link, link.id as number)}</li>
                    );
                })}
            </ul>
        ) : null;
    };

    private _createLink = (link: IMenuItemData, index: number) => {
        const editableLink: Msdyn365.ILinksData = {
            ariaLabel: link.ariaLabel,
            className: 'ms-header__header-link',
            linkText: link.linkText,
            linkUrl: link.linkURL,
            openInNewTab: false,
            role: 'link'
        };
        return <Msdyn365.Link key={index} link={editableLink} />;
    };

    private _renderCollapseMenu(): JSX.Element | null {
        return (
            <Collapse className='ms-nav__layer' isOpen={this.state.isNavOpen}>
                {this.state.isOnlyMobile ? this._renderMobileMenu() : this._renderDesktopMenu()}
            </Collapse>
        );
    }

    private _renderMobileMenu(): JSX.Element {
        const { MenuList, MobileBackButton, MobileDescriptionContainer, MobileDescriptionLabel, Navigation } = this.props.navProps;
        let levelClassName = `level-${(this.currentLevel + 1).toString()}`;
        return (
            <>
                <div className='ms-nav__close-button ms-nav-closer'>
                    <button aria-label='Close' onClick={this._closeNavMenu} />
                </div>
                <Module
                    {...Navigation}
                    className={classnames(
                        Navigation.className,
                        this.state.isOnlyMobile && this.state.activeMenu !== undefined
                            ? `${Navigation.className}--${levelClassName} child`
                            : 'parent'
                    )}
                >
                    <Node {...MenuList} tabIndex='-1'>
                        {this.state.isOnlyMobile && this.state.activeMenu !== undefined && (
                            <Node {...MobileDescriptionContainer} onClick={this._handleGoBack()}>
                                <Node {...MobileBackButton} onClick={this._handleGoBack()} />
                                <Node {...MobileDescriptionLabel}>{this.state.mobileViewLabelText}</Node>
                            </Node>
                        )}
                        {this._renderDisplay()}
                    </Node>
                </Module>
            </>
        );
    }

    private _renderDesktopMenu(): JSX.Element {
        const { MenuList, Navigation, DivContainer, ImageDivContainer, showCategoryImage, showPromotionalContent } = this.props.navProps;
        const navListClassName = `ms-nav__list ms-nav__list--level-${(this.currentLevel + 1).toString()}`;
        const isCategoryImageDisplay =
            !this.state.isOnlyMobile && showCategoryImage && this.state.categoryImage !== null && this.state.categoryImage !== undefined;
        const isPromotionalContentDisplay =
            !this.state.isOnlyMobile && showPromotionalContent && ArrayExtensions.hasElements(this.state.categoryImage);
        const divContainerClass =
            this.currentLevel < NavMenuConstants.two || isCategoryImageDisplay ? DivContainer!.className : 'ms-nav__default';
        this.currentLevel = NavMenuConstants.two;
        const categoryImages = this.state.categoryImage?.filter(image => !StringExtensions.isNullOrWhitespace(image.src));
        const imagesContainerKey = categoryImages?.map(item => item.src).join('-');

        return (
            <>
                <div className='ms-nav__close-button ms-nav-closer'>
                    <button aria-label='Close' onClick={this._closeNavMenu} />
                </div>
                <Module {...Navigation} className='ms-nav__disable-top-menu'>
                    <Node {...DivContainer} className={classnames(divContainerClass)}>
                        <Node
                            {...MenuList}
                            className={classnames(navListClassName, isCategoryImageDisplay && 'havecateImage')}
                            tabIndex='-1'
                        >
                            {this._renderDisplay()}
                        </Node>
                        {isCategoryImageDisplay && ArrayExtensions.hasElements(categoryImages) && (
                            <div className='category-image-container' key={imagesContainerKey}>
                                {ArrayExtensions.hasElements(categoryImages) &&
                                    categoryImages.map(item => (
                                        <Node {...ImageDivContainer} className={ImageDivContainer!.className} key={item.src}>
                                            {getCategoryImage(this.props, item.src ?? '', item.altText!.toString())}
                                            {isPromotionalContentDisplay &&
                                                this._renderPromotionalLink(item.altText, item.additionalProperties?.linkUrl)}
                                        </Node>
                                    ))}
                            </div>
                        )}
                    </Node>
                </Module>
            </>
        );
    }

    private _renderDrawerLink(item: IMenuItemData): JSX.Element | null {
        if (item.linkURL && !StringExtensions.isNullOrWhitespace(item.linkURL)) {
            return this._renderLinkMenuItem(item);
        } else if (!item.linkURL) {
            return this._renderSpanMenuItem(item);
        }
        return null;
    }

    private _renderDisplay(): JSX.Element[] {
        const { ListItem } = this.props.navProps;
        const menuItemData: IMenuItemData[] = this.props.navProps.menuItemData;
        const { activeMenu } = this.state;
        const menuItemList: JSX.Element[] = [];
        const levelClassName = `level-${this.currentLevel.toString()}`;

        // Mapping for menuSource classes
        const menuSourceClassMap: Record<string, string> = {
            Retail: 'ms-nav-item__retail',
            Cms: 'ms-nav-item__cms'
        };

        if (ArrayExtensions.hasElements(menuItemData)) {
            menuItemData[0].id = 1;
        }

        if (this.state.isOnlyMobile && activeMenu !== undefined && ArrayExtensions.hasElements(menuItemData)) {
            const menuItem = this._getFromSubMenu(undefined, menuItemData);

            if (menuItem) {
                menuItemList.push(
                    <Node key={menuItem.id} {...ListItem}>
                        {this._createMenuItemList(menuItem)} {` `}
                    </Node>
                );
            }
        } else {
            if (this.state.isOnlyMobile) {
                for (const [index, item] of menuItemData.entries()) {
                    menuItemList.push(
                        <Node
                            key={item.id}
                            {...ListItem}
                            ref={
                                this._shouldAddFocusMenuRef(index, this.state.mobileViewLabelText, item.linkText) ? this.menuItemRef : null
                            }
                        >
                            {this._createMenuItemList(item)}
                        </Node>
                    );
                }
            } else {
                for (const [, menuItem] of menuItemData.entries()) {
                    if (menuItem.id === this.state.menuItemId && menuItem.subMenu?.length) {
                        for (const [index, subMenuItem] of menuItem.subMenu.entries()) {
                            const extraClasses =
                                subMenuItem.menuSource && menuSourceClassMap[subMenuItem.menuSource]
                                    ? [menuSourceClassMap[subMenuItem.menuSource]]
                                    : [];
                            menuItemList.push(
                                <Node
                                    key={subMenuItem.id}
                                    {...ListItem}
                                    ref={
                                        this._shouldAddFocusMenuRef(index, this.state.mobileViewLabelText, subMenuItem.linkText)
                                            ? this.menuItemRef
                                            : null
                                    }
                                    className={classnames(
                                        `ms-nav__list--${levelClassName}__item`,
                                        menuItem.subMenu && 'havesubmenu',
                                        ...extraClasses
                                    )}
                                >
                                    {this._createMenuItemList(subMenuItem)}
                                </Node>
                            );
                        }
                    }
                }
            }
        }

        return menuItemList;
    }

    private _getFromSubMenu(parentMenuId?: number, menu?: IMenuItemData[]): IMenuItemData | null {
        if (!menu) {
            return null;
        }

        for (let index = 0; index < menu.length; index++) {
            const item: IMenuItemData = menu[Number(index)];

            if (item.id === this.state.activeMenu) {
                if (!NavigationMenuLinks.isBackTrack) {
                    this.setState({ parentMenu: parentMenuId });
                } else {
                    this.setState({ parentMenu: parentMenuId, mobileViewLabelText: item.linkText });
                }
                return item;
            }
            const found = this._getFromSubMenu(item.id, item.subMenu);
            if (found) {
                return found;
            }
        }
        return null;
    }

    private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
        const { subMenu, linkURL, id } = menuItemData;
        const { isOnlyMobile, activeMenu } = this.state;
        if (ArrayExtensions.hasElements(subMenu)) {
            if (isOnlyMobile && activeMenu !== undefined) {
                return this._renderSubMenu(subMenu, id);
            }
            if (!isOnlyMobile && linkURL) {
                return this._renderLinkMenuItem(menuItemData);
            }
            return this._renderButtonMenuItem(menuItemData);
        }
        if (linkURL && !StringExtensions.isNullOrWhitespace(linkURL)) {
            return this._renderLinkMenuItem(menuItemData, id);
        }
        if (!linkURL && !(isOnlyMobile && activeMenu !== undefined)) {
            return this._renderSpanMenuItem(menuItemData);
        }
        return null;
    }

    private _renderSubMenu(subMenuArray?: IMenuItemData[], activeMenu?: number): JSX.Element | null {
        let subMenus: IMenuItemData[] | undefined = subMenuArray;
        let currentLevel = ArrayExtensions.hasElements(subMenus) ? NavMenuConstants.three : NavMenuConstants.two;
        if (activeMenu === NavMenuConstants.rootMenu) {
            subMenus = subMenuArray!.filter(subMenuItem => subMenuItem.id && subMenuItem.id >= MenuTypeIndexId.Retail);
        }

        const { ListItem } = this.props.navProps;

        if (activeMenu && this.state.activeMenu !== activeMenu) {
            this.props.navProps.context.telemetry.error('Navigation Active menu content is empty, module wont render.');
            return null;
        }

        if (!subMenus || !ArrayExtensions.hasElements(subMenus)) {
            this.props.navProps.context.telemetry.error('Navigation Submenu content is empty, module wont render.');
            return null;
        }

        let levelClassName: string = '';
        const menuOptions = subMenus.map((option: IMenuItemData, index: number) => {
            levelClassName = `level-${currentLevel.toString()}`;
            const hasOptions = option.subMenu && ArrayExtensions.hasElements(option.subMenu);
            let menuItem: JSX.Element | null;
            if (hasOptions && this.state.isOnlyMobile) {
                menuItem = this._renderButtonMenuItem(option, activeMenu, index);
            } else if (this.currentLevel === NavMenuConstants.one || !hasOptions) {
                menuItem = this.getMenuItem(option, index);
            } else {
                menuItem = null;
            }

            const imagesource = option.imageSource ? option.imageSource : '';
            return (
                <Node
                    {...ListItem}
                    key={option.id}
                    onMouseOver={this.currentLevel === NavMenuConstants.two && this._updateCategoryImage(imagesource, option)}
                    className={classnames(`${ListItem.className}--${levelClassName}`)}
                    ref={this._shouldAddFocusMenuRef(index, this.state.mobileViewLabelText, option.linkText) ? this.menuItemRef : null}
                >
                    {menuItem}
                </Node>
            );
        });
        return this._renderMenu(currentLevel, menuOptions);
    }

    private getMenuItem(option: IMenuItemData, index: number): JSX.Element | null {
        let menuItem: JSX.Element | null;
        if (this.currentLevel === NavMenuConstants.two) {
            menuItem = option.linkURL ? this._renderLinkMenuItemLevelOne(option, index) : this._renderSpanMenuItem(option);
        } else {
            menuItem = option.linkURL ? this._renderLinkMenuItem(option, index) : this._renderSpanMenuItem(option);
        }
        return menuItem;
    }

    private _renderSubMenuDrawer(menuItem: IMenuItemData): JSX.Element | null {
        const levelClassName = `level-${this.currentLevel.toString()}`;
        const toggleButtonText = menuItem.linkText;
        const keyValue = this.state.drawerKeyValue;
        const buttonText = toggleButtonText !== undefined ? toggleButtonText : '';
        const keys = keyValue;
        const multiLevelSupportedMenu = this.props.navProps.config.menuLevelSupport ?? NavMenuConstants.three;

        let isDrawerOpen = false;
        if (keys[menuItem.id!]) {
            isDrawerOpen = true;
        }
        const colProps: ICollapseProps = { timeout: 0, isOpen: isDrawerOpen };

        return (
            <Drawer
                collapseProps={colProps}
                key={menuItem.id}
                className={classnames('ms-nav__drawer_desktop', levelClassName)}
                openGlyph='ms-nav__drawer-open'
                closeGlyph='ms-nav__drawer-close'
                glyphPlacement='start'
                toggleButtonText={buttonText}
                onToggle={this._bindDesktopCategoryClick(menuItem.id, isDrawerOpen)}
            >
                <div>
                    {menuItem.subMenu!.map((menuSubItem: IMenuItemData) => {
                        if (ArrayExtensions.hasElements(menuSubItem.subMenu)) {
                            this.currentLevel++;
                            if (this.currentLevel < multiLevelSupportedMenu) {
                                return this._renderSubMenuDrawer(menuSubItem);
                            }
                        }
                        return this._renderDrawerLink(menuSubItem);
                    })}
                </div>
            </Drawer>
        );
    }

    /**
     * Method to handle escape key event.
     * @param menuId -Number.
     * @param isDrawerOpen -Boolean.
     * @returns Void.
     */
    private readonly _bindDesktopCategoryClick = (menuId: number | undefined, isDrawerOpen: boolean) => (): void => {
        const { drawerKeyValue } = this.state;
        const newPair = { [menuId!]: !isDrawerOpen };
        this.setState({ drawerKeyValue: { ...drawerKeyValue, ...newPair } });
    };

    private _renderButtonMenuItem(option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null {
        return (
            <>
                <Node
                    key={index}
                    {...this.props.navProps.Button}
                    onClick={this.state.isOnlyMobile && this._handleDropdownToggle(option, activeMenu)}
                    aria-haspopup={!(this.state.activeMenu && this.state.activeMenu === option.id)}
                    aria-expanded={!!(this.state.activeMenu && this.state.activeMenu === option.id)}
                    data-parent={activeMenu}
                    aria-label={this.state.isOnlyMobile ? option.ariaLabel : undefined}
                >
                    {option.linkText}
                </Node>
                {!this.state.isOnlyMobile && option.subMenu && this._renderSubMenu(option.subMenu)}
            </>
        );
    }

    private _renderLinkMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
        const { Link } = this.props.navProps;
        const linkText = option.linkText ? option.linkText : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        return (
            <>
                <Node
                    {...Link}
                    key={index}
                    href={option.linkURL}
                    {...attributes}
                    target={option.shouldOpenNewTab ? '_blank' : undefined}
                    onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}
                >
                    {option.linkText}
                </Node>
                {!this.state.isOnlyMobile && option.subMenu && this._renderSubMenu(option.subMenu)}
            </>
        );
    }

    private _renderLinkMenuItemLevelOne(option: IMenuItemData, index?: number, isHoverEffect: boolean = true): JSX.Element | null {
        const { Link } = this.props.navProps;
        const linkText = option.linkText ? option.linkText : '';
        const imagesource = option.imageSource ? option.imageSource : '';
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        return (
            <Node
                {...Link}
                key={index}
                onMouseOver={isHoverEffect && this._updateCategoryImage(imagesource, option)}
                href={option.linkURL}
                {...attributes}
                onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}
            >
                {option.linkText}
            </Node>
        );
    }

    private _renderPromotionalLink(linkText?: string, linkUrl?: string): JSX.Element | null {
        const { Link } = this.props.navProps;
        this.payLoad.contentAction.etext = linkText;
        const attributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        if (linkText && linkUrl) {
            return (
                <Node {...Link} href={linkUrl} {...attributes} onClick={onTelemetryClick(this.telemetryContent, this.payLoad, linkText)}>
                    {linkText}
                </Node>
            );
        }
        return null;
    }

    private _renderSpanMenuItem(option: IMenuItemData, index?: number): JSX.Element | null {
        const { Span } = this.props.navProps;
        return (
            <Node key={index} {...Span}>
                {option.linkText}
            </Node>
        );
    }

    private _renderMenu(level: number, menuOptions: JSX.Element[]): JSX.Element | null {
        const { MenuList } = this.props.navProps;
        let levelClassName = `level-${level.toString()}`;
        return (
            <Node {...MenuList} className={classnames(`${MenuList.className}--${levelClassName}`)}>
                {menuOptions}
            </Node>
        );
    }

    /**
     * Handle Category image update.
     * @param categoryImageSource - Image source.
     * @param option - Menu Item data.
     * @returns Returns empty.
     */
    private readonly _updateCategoryImage = (categoryImageSource: string, option: IMenuItemData) => () => {
        const linkText = option.linkText ? option.linkText : '';
        const promotionalImage: Msdyn365.IImageData[] = [{ src: categoryImageSource, altText: linkText }];

        // Read category and promotional image in one array
        if (ArrayExtensions.hasElements(option.promotionalContent)) {
            for (const item of option.promotionalContent) {
                const imageSource = item.image.src;
                promotionalImage.push({
                    src: imageSource,
                    altText: item.text,
                    additionalProperties: { linkUrl: item.linkUrl.destinationUrl }
                });
            }
        }
        this.setState({
            categoryImage: ArrayExtensions.hasElements(promotionalImage) ? promotionalImage : [{ src: 'empty' }]
        });
    };

    /**
     * Handle dropdown toggle.
     * @param data - Menu Item data.
     * @param parentId - Number.
     * @returns Returns empty.
     */
    private readonly _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this.state.isOnlyMobile) {
            this.setState({
                activeMenu: data.id,
                parentMenu: parentId
            });
            if (this.props.navProps.showCategoryImage) {
                this._updateCategoryImage(data.imageSource!, data)();
            }
        } else {
            NavigationMenuLinks.isBackTrack = false;
            this.setState({
                activeMenu: data.id,
                mobileViewLabelText: data.linkText!,
                parentMenu: parentId
            });
        }
    };

    /**
     * Handle back navigation for menu items for mobile view.
     * @returns Returns empty.
     */
    private readonly _handleGoBack = () => () => {
        NavigationMenuLinks.isBackTrack = true;
        this.setState(previousState => ({ activeMenu: previousState.parentMenu }));
    };

    /**
     * Function to close the nav menu on click on body.
     * @param event - MouseEvent.
     */
    private readonly _handleClickOutside = (event: MouseEvent) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.target as Node)) {
            if (!this.state.isOnlyMobile) {
                this.setState({ activeMenu: 1, mobileViewLabelText: '', isNavOpen: false });
            } else {
                this.setState({ activeMenu: undefined, mobileViewLabelText: '', isNavOpen: false });
            }
        }
    };

    /**
     * Checks to assign a ref for focus.
     * @param index -Sub menu list index.
     * @param mobileViewLabelText -Mobile view label text.
     * @param optionLinkText -Sub menu item link text.
     * @returns Boolean.
     */
    private readonly _shouldAddFocusMenuRef = (index: number, mobileViewLabelText?: string, optionLinkText?: string) => {
        const firstIndex: number = 0;
        if (!NavigationMenuLinks.isBackTrack && index === firstIndex) {
            return true;
        }
        if (!optionLinkText || !mobileViewLabelText) {
            return false;
        }
        return mobileViewLabelText === optionLinkText;
    };

    /**
     * Function to handle nav menu close.
     */
    private readonly _closeNavMenu = (): void => {
        if (!this.state.isOnlyMobile) {
            this.setState({ activeMenu: 1, mobileViewLabelText: '', isNavOpen: false });
        } else {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '', isNavOpen: false });
        }
        (this.menuNode.current?.children[0] as HTMLElement).focus();
    };

    /**
     * Function to close the nav menu on esc key.
     * @param event - Event.
     */
    private readonly _escFunction = (event: React.KeyboardEvent) => {
        if (event.keyCode === this.escapeKey && !this.state.isOnlyMobile && this.state.isNavOpen) {
            this.setState({ activeMenu: 1, mobileViewLabelText: '', isNavOpen: false });
            (this.menuNode.current?.children[0] as HTMLElement).focus();
        }
    };

    /**
     * Custom update viewport.
     */
    private readonly _customUpdateViewport = (): void => {
        this.setState({ isOnlyMobile: this.isOnlyMobile });
    };
}

export default NavigationMenuLinks;
